import { useEffect, useMemo, useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Search, RefreshCw, Shield, Ban, TestTube } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format, formatDistanceToNow } from 'date-fns';
import { UserDetailDrawer } from './UserDetailDrawer';
import { cn } from '@/lib/utils';
import type { UserListItem } from './types';

type FilterChip = 'all' | 'admins' | 'banned' | 'test-stripe' | 'welcome' | 'paid';

const CHIP_LABELS: Record<FilterChip, string> = {
  all: 'All',
  admins: 'Admins',
  banned: 'Banned',
  'test-stripe': 'Test Stripe',
  welcome: 'Welcome',
  paid: 'Paid',
};

export function UserManagement() {
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterChip>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-list-users');
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setUsers(data?.users || []);
    } catch (err) {
      toast({
        title: 'Failed to load users',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let result = users;
    if (search) {
      const term = search.toLowerCase();
      result = result.filter((u) => u.email?.toLowerCase().includes(term));
    }
    switch (filter) {
      case 'admins':
        result = result.filter((u) => u.is_admin);
        break;
      case 'banned':
        result = result.filter((u) => u.is_banned);
        break;
      case 'test-stripe':
        result = result.filter((u) => u.use_test_stripe);
        break;
      case 'welcome':
        result = result.filter((u) => u.subscription_tier === 'welcome');
        break;
      case 'paid':
        result = result.filter(
          (u) => u.subscription_tier && ['starter', 'pro', 'enterprise'].includes(u.subscription_tier)
        );
        break;
    }
    return result;
  }, [users, search, filter]);

  const openDrawer = (id: string) => {
    setSelectedId(id);
    setDrawerOpen(true);
  };

  const counts = useMemo(
    () => ({
      all: users.length,
      admins: users.filter((u) => u.is_admin).length,
      banned: users.filter((u) => u.is_banned).length,
      'test-stripe': users.filter((u) => u.use_test_stripe).length,
      welcome: users.filter((u) => u.subscription_tier === 'welcome').length,
      paid: users.filter(
        (u) => u.subscription_tier && ['starter', 'pro', 'enterprise'].includes(u.subscription_tier)
      ).length,
    }),
    [users]
  );

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="rounded-xl border border-border/60 bg-card p-4 sm:p-5 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="gap-2 sm:w-auto"
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            Refresh
          </Button>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {(['all', 'admins', 'banned', 'test-stripe', 'welcome', 'paid'] as FilterChip[]).map((chip) => {
            const isActive = filter === chip;
            return (
              <button
                key={chip}
                type="button"
                onClick={() => setFilter(chip)}
                className={cn(
                  'inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium rounded-full border transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-border hover:bg-muted text-muted-foreground hover:text-foreground'
                )}
              >
                <span>{CHIP_LABELS[chip]}</span>
                <span
                  className={cn(
                    'inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1 rounded-full text-[10px]',
                    isActive ? 'bg-primary-foreground/20 text-primary-foreground' : 'bg-muted text-muted-foreground'
                  )}
                >
                  {counts[chip]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center py-12 text-sm text-muted-foreground">
            No users match your filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Email</TableHead>
                  <TableHead>Registered</TableHead>
                  <TableHead>Subscription</TableHead>
                  <TableHead className="text-right">Credits</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last activity</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((u) => (
                  <TableRow
                    key={u.id}
                    className={cn(
                      'cursor-pointer transition-colors',
                      u.is_banned && 'bg-destructive/5 hover:bg-destructive/10'
                    )}
                    onClick={() => openDrawer(u.id)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{u.email}</span>
                        {u.is_admin && (
                          <Badge
                            variant="outline"
                            className="text-xs gap-1 border-primary/30 text-primary bg-primary/5"
                          >
                            <Shield className="h-3 w-3" /> Admin
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {format(new Date(u.created_at), 'dd.MM.yyyy')}
                    </TableCell>
                    <TableCell>
                      {u.subscription_tier ? (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge
                            variant={u.subscription_status === 'active' ? 'default' : 'secondary'}
                            className="capitalize"
                          >
                            {u.subscription_tier}
                          </Badge>
                          {u.subscription_status && u.subscription_status !== 'active' && (
                            <Badge variant="outline" className="text-xs">
                              {u.subscription_status}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm tabular-nums">
                      {u.credits_remaining ?? '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {u.is_banned && (
                          <Badge variant="destructive" className="text-xs gap-1">
                            <Ban className="h-3 w-3" /> Banned
                          </Badge>
                        )}
                        {u.use_test_stripe && (
                          <Badge
                            variant="outline"
                            className="text-xs gap-1 border-warning/40 text-warning bg-warning/5"
                          >
                            <TestTube className="h-3 w-3" /> Test
                          </Badge>
                        )}
                        {!u.is_banned && !u.use_test_stripe && (
                          <Badge
                            variant="outline"
                            className="text-xs border-success/30 text-success bg-success/5"
                          >
                            Active
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {u.last_activity_at
                        ? formatDistanceToNow(new Date(u.last_activity_at), { addSuffix: true })
                        : 'Never'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDrawer(u.id);
                        }}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground px-1">
        Showing {filtered.length} of {users.length} users
      </p>

      <UserDetailDrawer
        userId={selectedId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onUserChanged={load}
      />
    </div>
  );
}
